import { Text, Page, View, StyleSheet } from '@react-pdf/renderer';
import {
  ProjectType,
  SalesRefTableType,
  ColumnType,
  AdsTableType,
} from '@/types/dataTypes';
import Footer from './footer';
import Header from './Header';
import { useMemo } from 'react';
import {
  getMarkerLabel,
  modifyValueBasedOnKeyForTable,
  removeStreetNumberFromString,
  formatNumber
} from '@/helpers';
import { ColumnEnum } from '@/enums';
import strings from '@/language/Swedish';
import { SalesSummaryResponseType, UserCustomerType } from '@/types/apiTypes';
import { IGNORE_LIST_REPORT } from '@/constants';
import { SortingType } from '@/components/table/helpers';
import { TableRowHeader } from './table/TableRowHeader';
import { getHeaderColumnWidth } from './table/helpers';
import { ascending, descending } from 'd3-array';
import { getSoldTableColumnOrder, getAdsTableColumnOrder } from '@/helpers/tableColumnOrder';

interface TablePageProps {
  project: ProjectType;
  hideClientData: boolean;
  username: string;
  table: 'sold' | 'ads';
  customer: UserCustomerType;
  sorting: SortingType | null;
  soldCalculations: SalesSummaryResponseType;
  adsCalculations: AdsTableType;
}

interface TableRowProps {
  data: [string, number][];
  index?: number;
  fontWeight?: 'normal' | 'bold';
  header?: boolean;
}

export interface TableRowHeaderProps {
  data: string[];
  index?: number;
  fontWeight?: 'normal' | 'bold';
  header?: boolean;
}

interface visibleColumns {
  ids: string[];
  labels: string[];
}

interface StringArray {
  [key: string]: any;
}

interface TableFooterProps {
  data: Record<string, string | number>;
  fontWeight?: 'normal' | 'bold';
  sortBasedOn: string[][];
  index: number;
  table: "sold" | "ads";
  showDistance: boolean;
}

const TableRow = ({
  data,
  index = 0,
  fontWeight = 'normal',
}: TableRowProps) => {
  const styles = StyleSheet.create({
    wrapper: {
      display: 'flex',
      flexDirection: 'row',
      paddingTop: 4,
      paddingBottom: 4,
      width: "100%"
    },
    text: {
      fontFamily: 'Chivo',
      fontSize: 8,
      paddingLeft: 5,
      paddingRight: 5,
      color: "black",
      display: "flex",
      flexWrap: 'nowrap'
    },
  });
  return (
    <View
      style={{
        ...styles.wrapper,
        backgroundColor: index % 2 === 0
          ? 'white'
          : '#bebbb4'
      }}>
      {data.map((item: [string, number], key: number) => {
        const camelCaseKey = item[0] //Refactor all camelcase in future
          .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
          .replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
        const style: object = getHeaderColumnWidth(strings.table.columns[camelCaseKey as keyof typeof strings.table.columns]);

        return (
          <View key={key} style={{ ...style, flex: "1 1", flexWrap: 'nowrap' }}>
            <Text style={{ ...styles.text, fontWeight: fontWeight }}>
              {item[1]}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

const TableFooter = ({
  data,
  fontWeight = 'normal',
  sortBasedOn,
  table,
  showDistance
}: TableFooterProps) => {
  const styles = StyleSheet.create({
    wrapper: {
      flexDirection: 'row',
      paddingVertical: 4,
      paddingTop: 5,
      width: '100%',
      borderTopWidth: 1,
      borderTopColor: 'black',
    },
    text: {
      fontFamily: 'Chivo',
      fontSize: 8,
      paddingHorizontal: 5,
    },
  });

  const transformKey = (key: string): string => {
    // Uses optional chaining to safely handle the replacement
    return key?.replace(/^average_/, '')
      .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  const sortedFooterData = useMemo(() => {
    const transformedData = Object.entries(data).reduce((acc, [key, value]) => {
      const newKey = transformKey(key);
      acc[newKey] = value;
      return acc;
    }, {} as Record<string, string | number>);
    // FIX IN FUTURE - You can migrate some of this to modifyValueBasedOnKeyForTable
    const renamedData = Object.entries(transformedData).reduce((acc, [key, value]) => {
      switch (key) {
        case 'distance':
          acc.street = `${showDistance ? strings.table.footers.distance + " " + value + "m": ""}`;
          break;
        case 'price':
          if (table == "sold") {
            acc.soldPrice = formatNumber(parseInt(value as string));
          } else {
            acc.price = formatNumber(parseInt(value as string));
          }
          break;
        case 'priceSqm':
          if (table == "sold") {
            acc.soldPriceSqm = formatNumber(parseInt(value as string));
          } else {
            acc.priceSqm = formatNumber(parseInt(value as string));
          }
          break;
        case 'priceToday':
          acc.recountedPrice = formatNumber(parseInt(value as string));
          break;
        case 'priceTodaySqm':
          acc.recountedPriceSqm = formatNumber(parseInt(value as string));
          break;
        case 'adPrice':
          acc.adPrice = formatNumber(parseInt(value as string));
          break;
        case 'taxationValue':
          acc.taxationValue = formatNumber(parseInt(value as string));
          break;
        case 'adPriceSqm':
          acc.adPriceSqm = formatNumber(parseInt(value as string));
          break;
        case 'constructionValueYear':
          acc.valuationYear = value;
          break;
        case 'monthlyFee':
          acc.monthlyFee = formatNumber(parseInt(value as string));
          break;
        case 'plotArea':
          acc.plotArea = formatNumber(parseInt(value as string));
          break;
        case 'priceTaxationRatio':
          acc.priceTaxationRatio = String(value).replace('.', ',');
          break;
        default:
          acc[key] = value;
          break;
      }
      return acc;
    }, {} as Record<string, string | number>);

    const sortOrder = sortBasedOn?.length ? sortBasedOn[0].map(([key]) => key) : [];

    return sortOrder.reduce((acc, key) => {
      const transformedKey = transformKey(key);
      acc[transformedKey] = renamedData[transformedKey] ?? '';
      return acc;
    }, {} as Record<string, string | number>);
  }, [data, sortBasedOn]);

  return (
    <View style={styles.wrapper}>
      {Object.entries(sortedFooterData).map(([key, value], index) => {
        const style = getHeaderColumnWidth(strings.table.columns[transformKey(key) as keyof typeof strings.table.columns])
        return (
          <View
            key={key + index}
            style={{ ...style, flex: 1, flexWrap: 'nowrap' }}
          >
            <Text style={{ ...styles.text, fontWeight }}>
              {value as string}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

function TablePage({
  project,
  hideClientData,
  username,
  table,
  sorting,
  customer,
  soldCalculations,
  adsCalculations,
}: TablePageProps) {
  const styles = StyleSheet.create({
    wrapper: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: '6mm',
      paddingRight: '6mm',
      zIndex: -2,
    },
    innerWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
    },
  });

  function filterSelectedItems(
    items: SalesRefTableType[] | AdsTableType[],
    selectedItems: Set<string>
  ): SalesRefTableType[] | AdsTableType[] | null {
    if (!selectedItems || selectedItems.size === 0) {
      return items;
    }
    return items.filter((column) => selectedItems.has(column.id)) as
      | SalesRefTableType[]
      | AdsTableType[];
  }

  //Maybe refactor to better algoritm in future
  function getVisibleColumns(columns: ColumnType[]): visibleColumns {
    const columnOrder = table === "sold" ? getSoldTableColumnOrder() : getAdsTableColumnOrder();
    const columnMap = new Map(columns.map(col => [col.id, col]));
  
    const sortedVisibleColumns = columnOrder
      .map(id => columnMap.get(id))
      .filter(column => 
        column && 
        column.isVisible && 
        !IGNORE_LIST_REPORT.includes(column.id as keyof typeof ColumnEnum | any)
      ) as ColumnType[];

    return {
      ids: sortedVisibleColumns.map(column => column.id),
      labels: sortedVisibleColumns.map(column => column.label),
    };
  }
  function motifyStreetPropertyForMap(
    data: SalesRefTableType[] | AdsTableType[],
  ): SalesRefTableType[] | AdsTableType[] {
    const returnData: SalesRefTableType[] | AdsTableType[] = data;

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    if (data) {
      data.forEach((item: StringArray, index: number) => {
        //For the markers
        const label: string = getMarkerLabel(index).toString();
        const value = hideClientData
          ? removeStreetNumberFromString(item[ColumnEnum.street])
          : item[ColumnEnum.street];
        const updatedStreetLabel: string = `${label}. ${item[ColumnEnum.adress]}`;
        returnData[index].street = (label && !hideClientData ? updatedStreetLabel : value) || ''
      });
    }
    return returnData;
  }
  function filterDataBasedOnVisibleColumns(
    data: SalesRefTableType[] | AdsTableType[] | null,
    visible: string[]
  ): [string, number][][] {
    const returnData: [string, number][][] = [];

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    data &&
      data.forEach((item: StringArray) => {
        const modifiedItem: Map<string, number> = new Map();
        visible.forEach((key: string) => {
          //If street append index get label for map
          let camelCaseKey = key
            .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
            .replace(/-([a-z])/g, (_, letter) => letter.toUpperCase()); //This is cause keys donrt match some are _ in name, fix in future.
          if (camelCaseKey == "adDeleteDate") { // FIX IN FUTURE
            camelCaseKey = ColumnEnum.adDeleteDate;
          }
          else {
            modifiedItem.set(
              key,
              (modifyValueBasedOnKeyForTable(item[camelCaseKey], key) as any) ||
              item[camelCaseKey]
            );
          }
        });
        returnData.push(Array.from(modifiedItem));
      });

    return returnData;
  }
  const selectedTableData: SalesRefTableType[] | AdsTableType[] | null =
    table == 'sold'
      ? filterSelectedItems(
        project.soldTableData as SalesRefTableType[],
        project.soldTableIds.selected as Set<string>
      )
      : filterSelectedItems(
        project.adsTableData as AdsTableType[],
        project.adsTableIds.selected as Set<string>
      );
  const selectedColumns: ColumnType[] =
    table == 'sold' ? project.soldTableReportColumns : project.adsTableReportColumns;
  const visibleColumns = getVisibleColumns(selectedColumns);
  const motifyStreetPropertyForMapColumns = motifyStreetPropertyForMap(selectedTableData);
  const sortingKey: string = sorting?.id.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()).replace(/-([a-z])/g, (_, letter) => letter.toUpperCase()) || '';
  const sortedData = motifyStreetPropertyForMapColumns ? motifyStreetPropertyForMapColumns.sort((a, b) => sorting?.desc ? descending(a[sortingKey as keyof typeof a], b[sortingKey as keyof typeof b]) : ascending(a[sortingKey as keyof typeof a], b[sortingKey as keyof typeof b])) : null;
  const filteredDataColumns: [string, number][][] =
    filterDataBasedOnVisibleColumns(sortedData, visibleColumns.ids);
  const adsAverages = project.adsTableIds.selected?.size === 0 ? project.adsSummary : adsCalculations;
  const soldAverages = project.soldTableIds.selected?.size === 0 ? project.soldSummary : soldCalculations;
  const showDistance: boolean =
    table == "sold"
      ? project.soldTableIds.selected?.size === 0
      : project.adsTableIds.selected?.size === 0;

  return (
    <Page size="A4" orientation="landscape" wrap>
      <Header title={`${project.name} - ${strings.navigation[table]}`} bgColor={customer?.primary_color} logo={customer?.logo_url} />
      <View style={styles.wrapper}>
        <View fixed>
          <TableRowHeader
            data={visibleColumns.labels}
            fontWeight="bold"
          />
        </View>

        <View style={styles.innerWrapper}>
          {filteredDataColumns.map(
            (item: [string, number][], index: number) => {
              return <TableRow data={item} key={index} index={index} />;
            }
          )}
          <TableFooter table={table} data={table == 'ads' ? adsAverages : soldAverages} sortBasedOn={filteredDataColumns} index={filteredDataColumns.length % 2} showDistance={showDistance} />
        </View>
      </View>
      <Footer hideClientData={hideClientData} username={username} />
    </Page>
  );
};

export default TablePage;
